import React, { useState, useEffect, useMemo } from 'react';
import { FiX, FiPrinter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';

/**
 * Modal for selecting which pallets to print labels for and choosing a layout.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - requestId: string (the request UUID whose pallets to print)
 *  - pallets: Array<{ palletId, palletLabel, itemCount, weight }> -- unique
 *    pallet records derived from the parent screen's data
 */
const PalletLabelModal = ({ isOpen, onClose, requestId, pallets }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [layout, setLayout] = useState('full');
  const [isPrinting, setIsPrinting] = useState(false);

  // Reset selections whenever the modal opens with new pallets
  useEffect(() => {
    if (isOpen && pallets?.length) {
      setSelectedIds(pallets.map(p => p.palletId));
    }
  }, [isOpen, pallets]);

  const allSelected = useMemo(
    () => pallets?.length > 0 && selectedIds.length === pallets.length,
    [selectedIds, pallets]
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pallets.map(p => p.palletId));
    }
  };

  const togglePallet = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handlePrint = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one pallet to print');
      return;
    }

    setIsPrinting(true);
    try {
      const html = await adminAPI.getPalletLabels(requestId, {
        palletIds: selectedIds,
        layout
      });
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.focus();
        // Allow the browser a moment to render before triggering print
        setTimeout(() => win.print(), 400);
      } else {
        toast.error('Pop-up blocked. Please allow pop-ups for this site.');
      }
    } catch (err) {
      toast.error(err.response?.data || 'Failed to load pallet labels');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleClose = () => {
    setSelectedIds([]);
    setLayout('full');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pallet-label-modal-title"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 id="pallet-label-modal-title" className="text-xl font-bold text-gray-900">
            Print Pallet Labels
          </h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-800" aria-label="Close dialog">
            <FiX size={24} />
          </button>
        </div>

        {/* Layout toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="palletLabelLayout"
                value="full"
                checked={layout === 'full'}
                onChange={() => setLayout('full')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Full Page (1 per page)</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="palletLabelLayout"
                value="half"
                checked={layout === 'half'}
                onChange={() => setLayout('half')}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Half Page (2 per page)</span>
            </label>
          </div>
        </div>

        {/* Select all / deselect all */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">
            {selectedIds.length} of {pallets?.length || 0} selected
          </span>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Pallet list */}
        <div className="border border-gray-200 rounded-md divide-y divide-gray-200 mb-4 max-h-64 overflow-y-auto">
          {(!pallets || pallets.length === 0) && (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              No pallets found for this request.
            </div>
          )}
          {pallets?.map(p => (
            <label
              key={p.palletId}
              className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(p.palletId)}
                onChange={() => togglePallet(p.palletId)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{p.palletLabel}</p>
                <p className="text-xs text-gray-500">
                  {p.itemCount} item{p.itemCount !== 1 ? 's' : ''}
                  {p.weight ? ` | ${p.weight} lbs` : ''}
                </p>
              </div>
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={isPrinting || selectedIds.length === 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <FiPrinter className="mr-2 h-4 w-4" />
            {isPrinting ? 'Loading...' : 'Print Selected'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PalletLabelModal;
